// ---------------------------------------------------------------------------
// The theme a person chose: system, light, dark or high contrast (WP35)
//
// The theme layer (entrypoints/sdr/src/client/tailwind.css) has a light base,
// a dark block and a high-contrast block. With nothing chosen the OS decides
// (prefers-color-scheme, prefers-contrast). A choice goes on <html> as
// `data-theme` and wins over the OS, because the OS blocks apply only to
// `:root:not([data-theme])`. It is remembered per browser in localStorage;
// "System" forgets it.
//
// The functions take the storage and the root as arguments, so they are tested
// without a browser; themeStorage() and initThemeChoice() are the only places
// that reach for the real window.
// ---------------------------------------------------------------------------

export type ThemeChoice = 'system' | 'light' | 'dark' | 'contrast';

export const THEME_STORAGE_KEY = 'sdr-theme';

/** In words: the system has no theme glyphs (I9), so the frame's SegmentedControl shows the labels. */
export const THEME_CHOICES: { value: ThemeChoice; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'contrast', label: 'High contrast' },
];

const VALUES = new Set<string>(THEME_CHOICES.map(c => c.value));

function isThemeChoice(value: unknown): value is ThemeChoice {
  return typeof value === 'string' && VALUES.has(value);
}

/** The saved choice, or "system" when there is none, it is unknown, or storage is blocked. */
export function readThemeChoice(storage?: Pick<Storage, 'getItem'>): ThemeChoice {
  try {
    const stored = storage?.getItem(THEME_STORAGE_KEY);
    return isThemeChoice(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/** Show a choice: an explicit theme goes on the root as data-theme; "system" takes it off. */
export function applyThemeChoice(
  choice: ThemeChoice,
  root: { setAttribute(name: string, value: string): void; removeAttribute(name: string): void },
): void {
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
}

/** Remember a choice for this browser; "system" forgets it. Blocked storage is not an error. */
export function saveThemeChoice(choice: ThemeChoice, storage?: Pick<Storage, 'setItem' | 'removeItem'>): void {
  try {
    if (choice === 'system') storage?.removeItem(THEME_STORAGE_KEY);
    else storage?.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // A private window may refuse storage: the choice then lasts for this page only.
  }
}

/** The browser's localStorage, or undefined where there is no window or reaching for it throws. */
export function themeStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}

/** Apply the saved choice to <html>. Called once at startup, before the app renders. */
export function initThemeChoice(): ThemeChoice {
  const choice = readThemeChoice(themeStorage());
  if (typeof document !== 'undefined') applyThemeChoice(choice, document.documentElement);
  return choice;
}
